// CLIPS-HM-01 · clip de video del evento, sobre la MISMA superficie del evento.
// Estados visibles: Preparando video · Ver clip · Video no disponible/expirado.
// La alerta y la imagen NO esperan al clip, y abrir el clip NO toma, modifica ni cierra el caso.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { refrescarClip, suscribirClip } from "@/lib/clipEstado";
import { Film, Loader2, VideoOff, X } from "lucide-react";

export default function ClipEvento({ eventoId, demo = false }) {
  const [info, setInfo] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const disparador = useRef(null);

  // Consulta COMPARTIDA por evento (v2.21.8): 500 ms los primeros 15 s y después cadencia normal,
  // con cancelación al salir y descarte de respuestas antiguas. Cambiar de evento cierra el
  // sondeo del anterior: no quedan consultas huérfanas ni datos de otro caso en pantalla.
  useEffect(() => {
    if (demo || !eventoId) { setInfo({ estado: "no_aplica" }); return; }
    setInfo(null);
    return suscribirClip(eventoId, setInfo);
  }, [eventoId, demo]);

  const cerrar = useCallback(() => {
    setAbierto(false);
    const b = disparador.current;
    if (b && b.isConnected) b.focus();
  }, []);

  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e) => { if (e.key === "Escape") cerrar(); };
    document.addEventListener("keydown", alTeclear);
    return () => document.removeEventListener("keydown", alTeclear);
  }, [abierto, cerrar]);

  if (!info || info.estado === "no_aplica") return null;
  const { estado } = info;

  const abrir = async (e) => {
    disparador.current = e?.currentTarget || null;
    // Se pide un permiso FRESCO al abrir: el anterior pudo caducar mientras el panel estaba quieto.
    try {
      const dato = await refrescarClip(eventoId);
      if (dato) setInfo(dato);
      if (dato?.estado === "listo") setAbierto(true);
      else if (dato?.estado === "preparando" || dato?.estado === "espera_agotada")
        toast.message("El video todavía se está preparando. Puedes seguir atendiendo la alerta.");
      else toast.message("El video ya no está disponible para este evento.");
    } catch {
      toast.error("No se pudo abrir el video. Puedes seguir atendiendo la alerta.");
    }
  };

  return (
    <section data-testid="clip-evento" data-estado={estado}
      className="rounded-xl border border-slate-200 bg-white dark:bg-[#14243A] p-4">
      <h3 className="mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#012472] dark:text-[#80B3FF]">
        <Film strokeWidth={1.8} className="h-4 w-4" /> Video del evento
      </h3>
      {estado === "listo" ? (
        <button data-testid="clip-ver-btn" onClick={abrir}
          className="inline-flex min-h-12 items-center gap-2 rounded-lg bg-[#012472] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#012472]/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3] dark:bg-[#01CBE3] dark:text-[#012472] dark:hover:bg-[#01B5CB]">
          <Film strokeWidth={2} className="h-4 w-4" /> Ver clip
          {Number.isFinite(info.duracion_seg) && info.duracion_seg > 0 && (
            <span className="font-mono text-xs font-semibold opacity-80">{Math.round(info.duracion_seg)} s</span>
          )}
        </button>
      ) : (
        <div data-testid={`clip-estado-${estado}`}
          className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:bg-[#192C47] px-3 py-3 text-sm text-slate-600 dark:text-slate-300">
          {estado === "preparando"
            ? <Loader2 strokeWidth={1.8} className="h-4 w-4 animate-spin text-slate-400" />
            : <VideoOff strokeWidth={1.8} className="h-4 w-4 text-slate-400" />}
          <span>
            {estado === "preparando" ? "Preparando video…"
              : estado === "espera_agotada" ? "El video aún no llega. Vuelve a abrir el evento más tarde."
              : estado === "expirado" ? `Video expirado (se conserva ${info.retencion_dias || ""} días)`
              : "Video no disponible para este evento"}
          </span>
        </div>
      )}
      {estado === "preparando" && (
        <p className="mt-2 text-[11px] text-slate-400">
          Puedes atender la alerta sin esperar al video.
        </p>
      )}

      {abierto && info.ticket && createPortal(
        <div data-testid="clip-modal" role="dialog" aria-modal="true" aria-label="Clip del evento"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          onClick={cerrar}>
          <div className="w-full max-w-2xl rounded-xl bg-[#0B1A2E] p-3" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-[#80B3FF]">Clip del evento</p>
              <button data-testid="clip-cerrar" onClick={cerrar} aria-label="Cerrar el clip" autoFocus
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]">
                <X strokeWidth={2} className="h-5 w-5" />
              </button>
            </div>
            <video data-testid="clip-video" controls autoPlay playsInline preload="metadata"
              className="w-full rounded-lg bg-black"
              src={`${API}/eventos/${eventoId}/clip?t=${encodeURIComponent(info.ticket)}`} />
            <p className="mt-2 text-[11px] text-slate-400">
              Reproducir el clip no toma, modifica ni cierra el caso.
            </p>
          </div>
        </div>, document.body)}
    </section>
  );
}
